import { createCreateAuthIntentId } from "@/utils/createAuthHandoffPersistence";

export const CREDIT_RESUME_ID_PARAM = "credit_resume_id";

const CREDIT_AUTH_INTENT_KEY = "create.credit.purchaseAfterAuth";
const CREDIT_AUTH_INTENT_PREFIX = `${CREDIT_AUTH_INTENT_KEY}.`;
const CREDIT_AUTH_INTENT_MAX_AGE_MS = 24 * 60 * 60 * 1000;

interface StoredCreditAuthIntent {
  intentId: string;
  credits: number;
  resumeKey: string;
  rememberedAt: number;
  offerToken?: string;
}

interface StoredCreditAuthIntents {
  intents: Record<string, StoredCreditAuthIntent>;
}

function creditIntentStorageKey(intentId: string): string {
  return `${CREDIT_AUTH_INTENT_PREFIX}${intentId}`;
}

function migrateLegacyCreditAuthIntents(): void {
  const serialized = window.localStorage.getItem(CREDIT_AUTH_INTENT_KEY);
  if (!serialized) return;
  const parsed = JSON.parse(serialized) as
    | Partial<StoredCreditAuthIntent>
    | Partial<StoredCreditAuthIntents>;
  let intents: Record<string, StoredCreditAuthIntent> = {};
  if ("resumeKey" in parsed && typeof parsed.resumeKey === "string") {
    const intentId = `legacy:${parsed.resumeKey}`;
    intents = {
      [intentId]: {
        intentId,
        credits: Number(parsed.credits),
        resumeKey: parsed.resumeKey,
        rememberedAt: Number(parsed.rememberedAt),
      },
    };
  } else if ("intents" in parsed && parsed.intents) {
    intents = parsed.intents;
  }

  for (const intent of Object.values(intents)) {
    const storageKey = creditIntentStorageKey(intent.intentId);
    if (!window.localStorage.getItem(storageKey)) {
      writeCreditAuthIntent(intent);
    }
  }
  window.localStorage.removeItem(CREDIT_AUTH_INTENT_KEY);
}

function readCreditAuthIntents(): Record<string, StoredCreditAuthIntent> {
  migrateLegacyCreditAuthIntents();
  const intents: Record<string, StoredCreditAuthIntent> = {};
  for (let index = 0; index < window.localStorage.length; index++) {
    const storageKey = window.localStorage.key(index);
    if (!storageKey?.startsWith(CREDIT_AUTH_INTENT_PREFIX)) continue;
    const serialized = window.localStorage.getItem(storageKey);
    if (!serialized) continue;
    const intent = JSON.parse(serialized) as StoredCreditAuthIntent;
    if (
      typeof intent.intentId === "string" &&
      creditIntentStorageKey(intent.intentId) === storageKey
    ) {
      intents[intent.intentId] = intent;
    }
  }
  return intents;
}

function writeCreditAuthIntent(intent: StoredCreditAuthIntent): void {
  const storageKey = creditIntentStorageKey(intent.intentId);
  const serialized = JSON.stringify(intent);
  window.localStorage.setItem(storageKey, serialized);
  if (window.localStorage.getItem(storageKey) !== serialized) {
    throw new Error("Credit auth intent was not persisted");
  }
}

function removeCreditAuthIntent(intentId: string): void {
  const storageKey = creditIntentStorageKey(intentId);
  window.localStorage.removeItem(storageKey);
  if (window.localStorage.getItem(storageKey) !== null) {
    throw new Error("Credit auth intent was not consumed");
  }
}

function isCurrent(intent: StoredCreditAuthIntent): boolean {
  const ageMs = Date.now() - Number(intent.rememberedAt);
  return (
    Number.isInteger(intent.credits) &&
    intent.credits > 0 &&
    Boolean(intent.intentId) &&
    Boolean(intent.resumeKey) &&
    Number.isFinite(ageMs) &&
    ageMs >= 0 &&
    ageMs <= CREDIT_AUTH_INTENT_MAX_AGE_MS
  );
}

function readCurrentCreditAuthIntents(): Record<
  string,
  StoredCreditAuthIntent
> {
  const intents = readCreditAuthIntents();
  const current = Object.fromEntries(
    Object.entries(intents).filter(([, intent]) => isCurrent(intent))
  );
  for (const intentId of Object.keys(intents)) {
    if (!current[intentId]) removeCreditAuthIntent(intentId);
  }
  return current;
}

export function rememberCreditPurchaseAfterAuth(
  credits: number,
  resumeKey?: string | null,
  preferredIntentId?: string | null,
  offerToken?: string
): string | null {
  if (
    typeof window === "undefined" ||
    !Number.isInteger(credits) ||
    credits <= 0 ||
    !resumeKey
  ) {
    return null;
  }
  try {
    const intentId = preferredIntentId || createCreateAuthIntentId();
    if (!intentId) return null;
    readCurrentCreditAuthIntents();
    writeCreditAuthIntent({
      intentId,
      credits,
      resumeKey,
      rememberedAt: Date.now(),
      offerToken: offerToken?.startsWith("credit_offer_")
        ? offerToken
        : undefined,
    });
    return intentId;
  } catch {
    return null;
  }
}

export function hasCreditPurchaseAfterAuth(
  intentId: string | null | undefined,
  expectedCredits: number,
  expectedResumeKey?: string | null
): boolean {
  if (typeof window === "undefined" || !intentId || !expectedResumeKey) {
    return false;
  }
  try {
    const intent = readCurrentCreditAuthIntents()[intentId];
    return Boolean(
      intent &&
      intent.credits === expectedCredits &&
      intent.resumeKey === expectedResumeKey
    );
  } catch {
    return false;
  }
}

export function hasPendingCreditPurchaseAfterAuth(
  intentId?: string | null,
  expectedCredits?: number
): boolean {
  if (typeof window === "undefined") return false;
  try {
    const intents = readCurrentCreditAuthIntents();
    if (intentId) {
      const intent = intents[intentId];
      return Boolean(
        intent && (!expectedCredits || intent.credits === expectedCredits)
      );
    }
    return Object.values(intents).some(
      intent => !expectedCredits || intent.credits === expectedCredits
    );
  } catch {
    return false;
  }
}

export function clearCreditPurchaseAfterAuth(intentId?: string | null): void {
  if (typeof window === "undefined" || !intentId) return;
  try {
    removeCreditAuthIntent(intentId);
  } catch {
    // Best effort cleanup.
  }
}

export function clearAllCreditPurchasesAfterAuth(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(CREDIT_AUTH_INTENT_KEY);
    const keys: string[] = [];
    for (let index = 0; index < window.localStorage.length; index++) {
      const storageKey = window.localStorage.key(index);
      if (storageKey?.startsWith(CREDIT_AUTH_INTENT_PREFIX)) {
        keys.push(storageKey);
      }
    }
    keys.forEach(storageKey => window.localStorage.removeItem(storageKey));
  } catch {
    // Best effort cleanup at a real identity boundary.
  }
}

export function consumeCreditPurchaseAfterAuth(
  intentId: string | null | undefined,
  expectedCredits: number,
  expectedResumeKey?: string | null
): boolean {
  const matches = hasCreditPurchaseAfterAuth(
    intentId,
    expectedCredits,
    expectedResumeKey
  );
  if (!matches || !intentId) return false;
  try {
    removeCreditAuthIntent(intentId);
    return true;
  } catch {
    return false;
  }
}

export type CreditPurchaseResumeOutcome = "completed" | "retry" | "unavailable";

/**
 * Hold the browser's cross-tab lock for the entire checkout handoff.
 *
 * The exact intent is durably claimed before its side effect starts, so a
 * waiting tab cannot open a second checkout. Failed automatic attempts remain
 * consumed and fall back to the visible manual purchase flow.
 */
export async function resumeCreditPurchaseAfterAuth(
  intentId: string,
  expectedCredits: number,
  expectedResumeKey: string,
  onResume: () => boolean | void | Promise<boolean | void>
): Promise<CreditPurchaseResumeOutcome> {
  if (
    typeof window === "undefined" ||
    typeof navigator === "undefined" ||
    !navigator.locks
  ) {
    return "unavailable";
  }

  const actionFailures: unknown[] = [];
  try {
    const outcome = await navigator.locks.request(
      `${CREDIT_AUTH_INTENT_KEY}:${intentId}`,
      async (): Promise<CreditPurchaseResumeOutcome> => {
        let intent: StoredCreditAuthIntent | undefined;
        try {
          intent = readCurrentCreditAuthIntents()[intentId];
          if (
            !intent ||
            intent.credits !== expectedCredits ||
            intent.resumeKey !== expectedResumeKey
          ) {
            return "unavailable";
          }
          // Claim durably before the side effect. A same-intent waiter cannot
          // start after this lock releases, even if successful cleanup fails.
          removeCreditAuthIntent(intentId);
        } catch {
          return "unavailable";
        }

        try {
          const succeeded = await onResume();
          if (succeeded !== false) return "completed";
        } catch (error) {
          actionFailures.push(error);
        }
        return "retry";
      }
    );
    if (actionFailures.length > 0) {
      throw actionFailures[0];
    }
    return outcome;
  } catch {
    if (actionFailures.length > 0) throw actionFailures[0];
    return "unavailable";
  }
}

export function creditPurchaseAfterAuthOffer(
  intentId: string
): string | undefined {
  try {
    return readCurrentCreditAuthIntents()[intentId]?.offerToken;
  } catch {
    return undefined;
  }
}
